import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, CardContent, Typography, Grid, Box, Button, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Paper, Chip,
    CircularProgress, Alert, IconButton, Menu, MenuItem, FormControlLabel,
    Checkbox, Tooltip, Badge, TextField, InputAdornment, Switch,
    FormControl, InputLabel, Select, Divider, Avatar, Dialog,
    DialogTitle, DialogContent, DialogActions, TablePagination
} from '@mui/material';
import { useNotify } from 'react-admin';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SportsIcon from '@mui/icons-material/Sports';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import GetAppIcon from '@mui/icons-material/GetApp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import StarIcon from '@mui/icons-material/Star';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;

// Funzione per recuperare il token JWT
const getJwt = () => {
    return localStorage.getItem("feathers-jwt");
};

function LeghePage() {
    const notify = useNotify();
    const navigate = useNavigate();

    // State principale
    const [leghe, setLeghe] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [fullscreen, setFullscreen] = React.useState(false);
    const [autoRefresh, setAutoRefresh] = React.useState(false);
    const [refreshInterval, setRefreshInterval] = React.useState(null);
    const [filterSport, setFilterSport] = React.useState('all');
    const [filterStatus, setFilterStatus] = React.useState('all');
    const [selectedLega, setSelectedLega] = React.useState(null);
    const [dialogOpen, setDialogOpen] = React.useState(false);

    // Paginazione
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    // Menu settings
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [visibleColumns, setVisibleColumns] = React.useState({
        id: true,
        nome: true,
        sport: true,
        note: true,
        status: true,
        ordinamento: true,
        createdAt: true,
        actions: true
    });

    // Caricamento leghe
    const fetchLeghe = React.useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = getJwt();
            if (!token) {
                throw new Error('Token non trovato');
            }

            const response = await axios.get(`${host}/tbl-lega`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data && response.data.data) {
                setLeghe(response.data.data);
            } else {
                setLeghe([]);
            }
        } catch (error) {
            console.error('Errore nel caricamento leghe:', error);
            setError('Errore nel caricamento delle leghe');
            notify('Errore nel caricamento delle leghe', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [notify]);

    // Effetti
    React.useEffect(() => {
        fetchLeghe();
    }, [fetchLeghe]);

    React.useEffect(() => {
        if (autoRefresh) {
            const interval = setInterval(fetchLeghe, 30000); // Refresh ogni 30 secondi
            setRefreshInterval(interval);
        } else if (refreshInterval) {
            clearInterval(refreshInterval);
            setRefreshInterval(null);
        }

        return () => {
            if (refreshInterval) {
                clearInterval(refreshInterval);
            }
        };
    }, [autoRefresh, fetchLeghe, refreshInterval]);

    // Funzioni di utilità
    const getStatusChip = (lega) => {
        if (lega.flAnnullato === 1) {
            return <Chip label="Annullata" color="error" size="small" icon={<CancelIcon />} />;
        }
        if (lega.flAttivo === 1) {
            return <Chip label="Attiva" color="success" size="small" icon={<CheckCircleIcon />} />;
        }
        return <Chip label="Inattiva" color="default" size="small" />;
    };

    const getSportName = (sportId) => {
        // Qui potresti fare una chiamata per ottenere il nome dello sport
        // Per ora restituisco un placeholder
        return `Sport ${sportId}`;
    };

    // Filtri
    const filteredLeghe = React.useMemo(() => {
        return leghe.filter(lega => {
            const matchesSearch = !searchTerm ||
                lega.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                lega.note?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesSport = filterSport === 'all' || lega.sportId?.toString() === filterSport;

            const matchesStatus = filterStatus === 'all' ||
                (filterStatus === 'active' && lega.flAttivo === 1) ||
                (filterStatus === 'inactive' && lega.flAttivo === 0) ||
                (filterStatus === 'cancelled' && lega.flAnnullato === 1);

            return matchesSearch && matchesSport && matchesStatus;
        });
    }, [leghe, searchTerm, filterSport, filterStatus]);

    // Paginazione
    const paginatedLeghe = React.useMemo(() => {
        const startIndex = page * rowsPerPage;
        return filteredLeghe.slice(startIndex, startIndex + rowsPerPage);
    }, [filteredLeghe, page, rowsPerPage]);

    // Handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSettingsClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleSettingsClose = () => {
        setAnchorEl(null);
    };

    const handleColumnToggle = (column) => {
        setVisibleColumns(prev => ({
            ...prev,
            [column]: !prev[column]
        }));
    };

    const handleLegaClick = (lega, tab = undefined) => {
        let url = `/leghe/${lega.id}`;
        if (tab) url += `?tab=${tab}`;
        navigate(url);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedLega(null);
    };

    const handleEditLega = (legaId) => {
        navigate(`/tbl-lega/${legaId}`);
    };

    const handleCreateLega = () => {
        navigate('/tbl-lega/create');
    };

    // Render
    return (
        <Box sx={{
            p: 3,
            minHeight: fullscreen ? '100vh' : 'auto',
            position: fullscreen ? 'fixed' : 'relative',
            top: fullscreen ? 0 : 'auto',
            left: fullscreen ? 0 : 'auto',
            right: fullscreen ? 0 : 'auto',
            bottom: fullscreen ? 0 : 'auto',
            zIndex: fullscreen ? 9999 : 'auto',
            backgroundColor: fullscreen ? 'white' : 'transparent'
        }}>
            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    <CardContent>
                        <Grid container alignItems="center" spacing={2}>
                            <Grid item>
                                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                                    <EmojiEventsIcon sx={{ fontSize: 32, color: 'white' }} />
                                </Avatar>
                            </Grid>
                            <Grid item xs>
                                <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                                    Gestione Leghe
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                    Visualizza e gestisci tutte le leghe sportive
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Tooltip title="Crea nuova lega">
                                        <IconButton
                                            onClick={handleCreateLega}
                                            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}
                                        >
                                            <AddCircleIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Aggiorna">
                                        <IconButton
                                            onClick={fetchLeghe}
                                            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}
                                        >
                                            <RefreshIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={fullscreen ? "Esci da schermo intero" : "Schermo intero"}>
                                        <IconButton
                                            onClick={() => setFullscreen(!fullscreen)}
                                            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}
                                        >
                                            {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Impostazioni">
                                        <IconButton
                                            onClick={handleSettingsClick}
                                            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}
                                        >
                                            <SettingsIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </motion.div>

            {/* Filtri e Ricerca */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
            >
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    placeholder="Cerca per nome o note..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchTerm && (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setSearchTerm('')} size="small">
                                                    <ClearIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Sport</InputLabel>
                                    <Select
                                        value={filterSport}
                                        onChange={(e) => setFilterSport(e.target.value)}
                                        label="Sport"
                                    >
                                        <MenuItem value="all">Tutti gli sport</MenuItem>
                                        <MenuItem value="1">Calcio</MenuItem>
                                        <MenuItem value="2">Basket</MenuItem>
                                        <MenuItem value="3">Pallavolo</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Stato</InputLabel>
                                    <Select
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                        label="Stato"
                                    >
                                        <MenuItem value="all">Tutti</MenuItem>
                                        <MenuItem value="active">Attive</MenuItem>
                                        <MenuItem value="inactive">Inattive</MenuItem>
                                        <MenuItem value="cancelled">Annullate</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={autoRefresh}
                                            onChange={(e) => setAutoRefresh(e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label="Auto-refresh"
                                />
                            </Grid>
                        </Grid>

                        {/* Statistiche */}
                        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Chip
                                label={`Totale: ${leghe.length}`}
                                color="primary"
                                variant="outlined"
                            />
                            <Chip
                                label={`Attive: ${leghe.filter(l => l.flAttivo === 1).length}`}
                                color="success"
                                variant="outlined"
                            />
                            <Chip
                                label={`Inattive: ${leghe.filter(l => l.flAttivo === 0).length}`}
                                color="default"
                                variant="outlined"
                            />
                            <Chip
                                label={`Annullate: ${leghe.filter(l => l.flAnnullato === 1).length}`}
                                color="error"
                                variant="outlined"
                            />
                            <Chip
                                label={`Filtrate: ${filteredLeghe.length}`}
                                color="info"
                                variant="outlined"
                            />
                        </Box>
                    </CardContent>
                </Card>
            </motion.div>

            {/* Tabella */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
            >
                <Card>
                    <CardContent sx={{ p: 0 }}>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : error ? (
                            <Alert severity="error" sx={{ m: 2 }}>
                                {error}
                            </Alert>
                        ) : (
                            <>
                                <TableContainer component={Paper} elevation={0}>
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                {visibleColumns.id && (
                                                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
                                                        ID
                                                    </TableCell>
                                                )}
                                                {visibleColumns.nome && (
                                                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
                                                        Nome Lega
                                                    </TableCell>
                                                )}
                                                {visibleColumns.sport && (
                                                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
                                                        Sport
                                                    </TableCell>
                                                )}
                                                {visibleColumns.note && (
                                                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
                                                        Note
                                                    </TableCell>
                                                )}
                                                {visibleColumns.status && (
                                                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
                                                        Stato
                                                    </TableCell>
                                                )}
                                                {visibleColumns.ordinamento && (
                                                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
                                                        Ordinamento
                                                    </TableCell>
                                                )}
                                                {visibleColumns.createdAt && (
                                                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
                                                        Creata il
                                                    </TableCell>
                                                )}
                                                {visibleColumns.actions && (
                                                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
                                                        Azioni
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paginatedLeghe.map((lega, index) => (
                                                <TableRow
                                                    key={lega.id}
                                                    hover
                                                    sx={{
                                                        cursor: 'pointer',
                                                        '&:hover': { bgcolor: '#f9f9f9' }
                                                    }}
                                                    onClick={() => handleLegaClick(lega)}
                                                >
                                                    {visibleColumns.id && (
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                #{lega.id}
                                                            </Typography>
                                                        </TableCell>
                                                    )}
                                                    {visibleColumns.nome && (
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <EmojiEventsIcon color="primary" />
                                                                <Box>
                                                                    <Typography variant="body2" fontWeight="bold">
                                                                        {lega.nome || 'N/A'}
                                                                    </Typography>
                                                                    {lega.flVisibile === 1 && (
                                                                        <Chip label="Visibile" size="small" color="info" />
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                    )}
                                                    {visibleColumns.sport && (
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <SportsIcon color="action" />
                                                                <Typography variant="body2">
                                                                    {getSportName(lega.sportId)}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                    )}
                                                    {visibleColumns.note && (
                                                        <TableCell>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    maxWidth: 200,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                {lega.note || '-'}
                                                            </Typography>
                                                        </TableCell>
                                                    )}
                                                    {visibleColumns.status && (
                                                        <TableCell>
                                                            {getStatusChip(lega)}
                                                        </TableCell>
                                                    )}
                                                    {visibleColumns.ordinamento && (
                                                        <TableCell>
                                                            <Chip
                                                                label={lega.ordinamento || 0}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                    )}
                                                    {visibleColumns.createdAt && (
                                                        <TableCell>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {lega.createdAt ?
                                                                    new Date(lega.createdAt).toLocaleDateString('it-IT') :
                                                                    'N/A'
                                                                }
                                                            </Typography>
                                                        </TableCell>
                                                    )}
                                                    {visibleColumns.actions && (
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                                <Tooltip title="Visualizza dettagli">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleLegaClick(lega);
                                                                        }}
                                                                    >
                                                                        <InfoIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Modifica">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleEditLega(lega.id);
                                                                        }}
                                                                    >
                                                                        <EditIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* Paginazione */}
                                <TablePagination
                                    component="div"
                                    count={filteredLeghe.length}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    labelRowsPerPage="Righe per pagina:"
                                    labelDisplayedRows={({ from, to, count }) =>
                                        `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`
                                    }
                                />
                            </>
                        )}
                    </CardContent>
                </Card>
            </motion.div>

            {/* Menu Impostazioni */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleSettingsClose}
            >
                <MenuItem>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Colonne visibili:
                    </Typography>
                </MenuItem>
                <Divider />
                {Object.entries(visibleColumns).map(([column, visible]) => (
                    <MenuItem key={column}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={visible}
                                    onChange={() => handleColumnToggle(column)}
                                    size="small"
                                />
                            }
                            label={column.charAt(0).toUpperCase() + column.slice(1)}
                        />
                    </MenuItem>
                ))}
            </Menu>

            {/* Dialog Dettagli Lega */}
            <Dialog
                open={dialogOpen}
                onClose={handleDialogClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <EmojiEventsIcon color="primary" />
                        <Typography variant="h6">
                            Dettagli Lega: {selectedLega?.nome}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedLega && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary">ID</Typography>
                                <Typography variant="body1" sx={{ mb: 2 }}>#{selectedLega.id}</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary">Nome</Typography>
                                <Typography variant="body1" sx={{ mb: 2 }}>{selectedLega.nome || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary">Sport ID</Typography>
                                <Typography variant="body1" sx={{ mb: 2 }}>{getSportName(selectedLega.sportId)}</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary">Stato</Typography>
                                <Box sx={{ mb: 2 }}>
                                    {getStatusChip(selectedLega)}
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="text.secondary">Note</Typography>
                                <Typography variant="body1" sx={{ mb: 2 }}>{selectedLega.note || 'Nessuna nota'}</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary">Ordinamento</Typography>
                                <Typography variant="body1" sx={{ mb: 2 }}>{selectedLega.ordinamento || 0}</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary">Visibile</Typography>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    {selectedLega.flVisibile === 1 ? 'Sì' : 'No'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary">Creata il</Typography>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    {selectedLega.createdAt ?
                                        new Date(selectedLega.createdAt).toLocaleString('it-IT') :
                                        'N/A'
                                    }
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary">Aggiornata il</Typography>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    {selectedLega.updatedAt ?
                                        new Date(selectedLega.updatedAt).toLocaleString('it-IT') :
                                        'N/A'
                                    }
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>Chiudi</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            handleEditLega(selectedLega.id);
                            handleDialogClose();
                        }}
                        startIcon={<EditIcon />}
                    >
                        Modifica
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default LeghePage;
